import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ArrowUpRight, ArrowDownRight, BookPlus } from "lucide-react";

const LogTradeDialog = ({ open, onOpenChange, context, onSubmit }) => {
  const [side, setSide] = useState("Long");
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [exitPrice, setExitPrice] = useState("");
  const [note, setNote] = useState("");

  // Reset fields each time the dialog opens for a new context
  useEffect(() => {
    if (open) {
      setSide("Long"); setEntry(""); setStop(""); setExitPrice(""); setNote("");
    }
  }, [open]);

  const rr = useMemo(() => {
    const e = parseFloat(entry), s = parseFloat(stop), x = parseFloat(exitPrice);
    if (isNaN(e) || isNaN(s) || isNaN(x)) return null;
    const risk = Math.abs(e - s), reward = Math.abs(x - e);
    if (!risk) return null;
    return reward / risk;
  }, [entry, stop, exitPrice]);

  const submit = () => {
    onSubmit({
      symbol: context?.symbol,
      interval: context?.interval,
      side,
      entry: entry === "" ? null : parseFloat(entry),
      stop: stop === "" ? null : parseFloat(stop),
      exit: exitPrice === "" ? null : parseFloat(exitPrice),
      rr: rr != null ? Number(rr.toFixed(2)) : null,
      note: note || `Logged from chart (${context?.interval || ""})`,
    });
  };

  const field = "w-full bg-[rgba(10,17,33,0.7)] border border-[color:var(--ws-border)] rounded-lg px-3 py-2 text-[14px] text-white outline-none focus:border-[color:var(--ws-blue)] transition-colors";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="ws-dialog max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="ws-heading text-white flex items-center gap-2">
            <span className="ws-icon-chip w-8 h-8"><BookPlus className="w-4 h-4" /></span>
            Log Trade — {context?.symbol}
            <span className="text-[11px] text-[color:var(--ws-muted)] font-normal">{context?.interval}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Side */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Side</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <button onClick={() => setSide("Long")} className={`py-2 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 border transition-colors ${side === "Long" ? "bg-[rgba(52,211,153,0.15)] border-[rgba(52,211,153,0.5)] ws-up" : "border-[color:var(--ws-border)] text-[color:var(--ws-muted)]"}`}>
                <ArrowUpRight className="w-4 h-4" /> Long
              </button>
              <button onClick={() => setSide("Short")} className={`py-2 rounded-lg text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 border transition-colors ${side === "Short" ? "bg-[rgba(248,113,113,0.15)] border-[rgba(248,113,113,0.5)] ws-down" : "border-[color:var(--ws-border)] text-[color:var(--ws-muted)]"}`}>
                <ArrowDownRight className="w-4 h-4" /> Short
              </button>
            </div>
          </div>

          {/* Entry / Stop / Exit */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Entry</label>
              <input type="number" value={entry} onChange={(e) => setEntry(e.target.value)} placeholder="0.00" className={`${field} mt-1.5`} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Stop</label>
              <input type="number" value={stop} onChange={(e) => setStop(e.target.value)} placeholder="0.00" className={`${field} mt-1.5`} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Exit</label>
              <input type="number" value={exitPrice} onChange={(e) => setExitPrice(e.target.value)} placeholder="0.00" className={`${field} mt-1.5`} />
            </div>
          </div>

          {/* R:R */}
          <div className="ws-price-cell p-3 flex items-center justify-between">
            <span className="text-[12px] text-[color:var(--ws-muted)]">Reward : Risk</span>
            <span className={`ws-heading font-bold text-[18px] ${rr == null ? "text-[color:var(--ws-muted)]" : rr >= 1 ? "ws-up" : "ws-down"}`}>
              {rr == null ? "—" : `1 : ${rr.toFixed(2)}`}
            </span>
          </div>

          {/* Note */}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-[color:var(--ws-muted)]">Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Setup, reasoning, emotion…" className={`${field} mt-1.5 resize-none`} />
          </div>
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="ws-btn-ghost px-4 py-2 rounded-lg text-[13px] font-medium">Cancel</button>
          <button onClick={submit} className="ws-btn-primary px-5 py-2 rounded-lg text-[13px] font-semibold inline-flex items-center gap-2">
            <BookPlus className="w-4 h-4" /> Save to Journal
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogTradeDialog;
